import api from '../api';
import {
    SpeakingExercise,
    SpeakingSession,
    SpeakingAttempt,
    StartSessionRequest,
    AudioUploadRequest,
    CreateExerciseFromLessonRequest,
    ExerciseFilters,
    PaginatedResponse,
    AdaptivePracticeRecommendation,
} from './types';

const baseURL = '/speaking/v1';
class SpeakingService {
    // ============== EXERCISE MANAGEMENT ==============

    async getExercises(filters?: ExerciseFilters): Promise<PaginatedResponse<SpeakingExercise>> {
        const params = new URLSearchParams();

        if (filters) {
            Object.entries(filters).forEach(([key, value]) => {
                if (value !== undefined && value !== null && value !== '') {
                    params.append(key, value.toString());
                }
            });
        }

        const response = await api.get(`${baseURL}/exercises/?${params}`);
        return response.data;
    }

    async getExerciseById(exerciseId: string): Promise<SpeakingExercise> {
        const response = await api.get(`${baseURL}/exercises/${exerciseId}/`);
        return response.data;
    }

    async getExercisePhonemeDetails(exerciseId: string): Promise<any[]> {
        const response = await api.get(`${baseURL}/exercises/${exerciseId}/phoneme_details/`);
        return response.data;
    }

    async getRecommendedExercises(): Promise<AdaptivePracticeRecommendation[]> {
        const response = await api.get(`${baseURL}/exercises/recommended/`);
        return response.data;
    }

    async createExerciseFromLesson(request: CreateExerciseFromLessonRequest): Promise<SpeakingExercise> {
        const response = await api.post(`${baseURL}/create-from-lesson/`, request);
        return response.data;
    }

    // ============== SESSION MANAGEMENT ==============

    async getSessions(page: number = 1, pageSize: number = 20): Promise<PaginatedResponse<SpeakingSession>> {
        const response = await api.get(`${baseURL}/sessions/?page=${page}&page_size=${pageSize}`);
        return response.data;
    }

    async getSessionById(sessionId: string): Promise<SpeakingSession> {
        const response = await api.get(`${baseURL}/sessions/${sessionId}/`);
        return response.data;
    }

    async startSession(request: StartSessionRequest): Promise<SpeakingSession> {
        const response = await api.post(`${baseURL}/sessions/start_session/`, request);
        return response.data;
    }

    async endSession(sessionId: string): Promise<SpeakingSession> {
        const response = await api.post(`${baseURL}/sessions/${sessionId}/end_session/`);
        return response.data;
    }

    async completeSession(sessionId: string, completionData?: any): Promise<any> {
        const formData = new FormData();
        formData.append('session_id', completionData.session_id);
        formData.append('performance_data', JSON.stringify(completionData.performance_data));
        formData.append('attempt_number', completionData.audio_submission.attempt_number);
        formData.append('audio_file', completionData.audio_submission.audio_file);
        formData.append('audio_duration', completionData.audio_submission.audio_duration);


        const response = await api.post(`${baseURL}/sessions/${sessionId}/complete-session/`, formData, {
            headers: {
                'Content-Type': 'multipart/form-data',
            },
        });
        return response.data;
    }

    async getSessionAttempts(sessionId: string): Promise<SpeakingAttempt[]> {
        const response = await api.get(`${baseURL}/sessions/${sessionId}/attempts/`);
        return response.data;
    }

    // ============== AUDIO PROCESSING ==============

    async uploadAudio(request: AudioUploadRequest): Promise<SpeakingAttempt> {
        const formData = new FormData();
        formData.append('session_id', request.session_id);
        formData.append('attempt_number', request.attempt_number.toString());
        formData.append('audio_file', request.audio_file);
        formData.append('audio_duration', request.audio_duration.toString());

        const response = await api.post(`${baseURL}/upload-audio/`, formData, {
            headers: {
                'Content-Type': 'multipart/form-data',
            },
        });

        return response.data;
    }

    // ============== PRONUNCIATION ALERTS & NOTIFICATIONS ==============

    async getMispronunciationAlerts(days: number = 7): Promise<{
        phonemes: Array<{
            phoneme: string;
            error_count: number;
            accuracy_rate: number;
            last_error: string;
            severity: 'minor' | 'moderate' | 'major';
        }>;
        should_notify: boolean;
        notification_message: string;
        total_errors: number;
        improvement_trend: 'improving' | 'stable' | 'declining';
        practice_recommendations: string[];
    }> {
        const response = await api.get(`${baseURL}/mispronunciation-alerts/?days=${days}`);
        return response.data;
    }

    async createPronunciationAlert(data: {
        phoneme: string;
        error_type: string;
        severity: 'minor' | 'moderate' | 'major';
        message: string;
    }): Promise<{ notification_id: string; message: string }> {
        const response = await api.post(`${baseURL}/create-pronunciation-alert/`, data);
        return response.data;
    }

    async dismissPronunciationAlert(phoneme: string): Promise<{ message: string }> {
        const response = await api.post(`${baseURL}/dismiss-pronunciation-alert/`, { phoneme });
        return response.data;
    }

    // ============== PHONEME-BY-PHONEME PLAYBACK ==============

    async getPhonemeTimings(attemptId: string): Promise<
        Array<{
            phoneme: string;
            start_time: number;
            end_time: number;
            accuracy: number;
            user_audio_segment: string;
            reference_audio_segment: string;
            has_error?: boolean;
            severity?: 'minor' | 'moderate' | 'major';
        }>
    > {
        const response = await api.get(`${baseURL}/phoneme-timings/${attemptId}/`);
        return response.data;
    }

    async generatePhonemeAudio(
        phoneme: string,
        voice: 'male' | 'female' = 'female',
    ): Promise<{
        audio_url: string;
        phoneme: string;
        ipa_notation: string;
        voice: string;
    }> {
        const response = await api.post(`${baseURL}/generate-phoneme-audio/`, {
            phoneme,
            voice,
        });
        return response.data;
    }

    // ============== WAVEFORM & STRESS ANALYSIS ==============

    async getWaveformData(attemptId: string): Promise<{
        user_waveform: Array<{ time: number; amplitude: number; frequency?: number }>;
        reference_waveform: Array<{ time: number; amplitude: number; frequency?: number }>;
        stress_markers: Array<{
            start_time: number;
            end_time: number;
            stress_type: 'primary' | 'secondary' | 'unstressed';
            intensity: number;
        }>;
        error_markers: Array<{
            start_time: number;
            end_time: number;
            error_type: string;
            severity: 'minor' | 'moderate' | 'major';
            phoneme: string;
        }>;
    }> {
        const response = await api.get(`${baseURL}/waveform-data/${attemptId}/`);
        return response.data;
    }

    // ============== GPT FEEDBACK ==============

    async getConversationalFeedback(
        attemptId: string,
        tone: 'playful' | 'serious' | 'explanatory' | 'encouraging' = 'encouraging',
    ): Promise<{
        overall_feedback: string;
        phoneme_feedback: Array<{
            phoneme: string;
            feedback: string;
            suggestion: string;
            example_words: string[];
        }>;
        prosody_feedback: {
            stress_feedback: string;
            intonation_feedback: string;
            rhythm_feedback: string;
        };
        practice_recommendations: Array<{
            type: 'minimal_pairs' | 'shadowing' | 'repetition';
            description: string;
            exercises: string[];
        }>;
        encouragement: string;
        next_steps: string[];
    }> {
        const response = await api.get(`${baseURL}/conversational-feedback/${attemptId}/?tone=${tone}`);
        return response.data;
    }

    async updateFeedbackTone(
        sessionId: string,
        tone: 'playful' | 'serious' | 'explanatory' | 'encouraging',
    ): Promise<{ message: string }> {
        const response = await api.post(`${baseURL}/update-feedback-tone/`, {
            session_id: sessionId,
            tone,
        });
        return response.data;
    }

    // ============== RECOMMENDATIONS ==============

    async rateRecommendation(recommendationId: string, rating: number): Promise<{ message: string }> {
        const response = await api.post(`${baseURL}/rate-recommendation/`, {
            recommendation_id: recommendationId,
            rating,
        });
        return response.data;
    }

    // ============== HELPER METHODS ==============

    getExerciseTypeLabel(type: string): string {
        const labels: Record<string, string> = {
            phoneme_practice: 'Phoneme Practice',
            word_pronunciation: 'Word Pronunciation',
            sentence_reading: 'Sentence Reading',
            conversation: 'Conversation Practice',
            storytelling: 'Storytelling',
            minimal_pairs: 'Minimal Pairs',
            shadowing: 'Shadowing',
            free_speech: 'Free Speech',
            prosody_practice: 'Prosody Practice',
        };
        return labels[type] || type;
    }

    getDifficultyColor(difficulty: string): string {
        const colors: Record<string, string> = {
            beginner: '#4CAF50',
            intermediate: '#FF9800',
            advanced: '#F44336',
        };
        return colors[difficulty] || '#9E9E9E';
    }

    getScoreColor(score: number): string {
        if (score >= 90) return '#4CAF50'; // Green
        if (score >= 80) return '#8BC34A'; // Light Green
        if (score >= 70) return '#FFC107'; // Amber
        if (score >= 60) return '#FF9800'; // Orange
        return '#F44336'; // Red
    }

    getStressColor(stressType: 'primary' | 'secondary' | 'unstressed'): string {
        switch (stressType) {
            case 'primary':
                return '#4CAF50'; // Green
            case 'secondary':
                return '#2196F3'; // Blue
            case 'unstressed':
                return '#9E9E9E'; // Gray
            default:
                return '#9E9E9E';
        }
    }

    getErrorColor(severity: 'minor' | 'moderate' | 'major'): string {
        switch (severity) {
            case 'minor':
                return '#FFEB3B'; // Yellow
            case 'moderate':
                return '#FF9800'; // Orange
            case 'major':
                return '#F44336'; // Red
            default:
                return '#9E9E9E';
        }
    }

    formatDuration(seconds: number): string {
        const mins = Math.floor(seconds / 60);
        const secs = Math.floor(seconds % 60);
        return `${mins}:${secs.toString().padStart(2, '0')}`;
    }

    formatPracticeTime(timeString: string): string {
        // Convert Django duration string to readable format
        const match = timeString.match(/(\d+):(\d+):(\d+)/);
        if (match) {
            const hours = parseInt(match[1]);
            const minutes = parseInt(match[2]);
            const seconds = parseInt(match[3]);

            if (hours > 0) {
                return `${hours}h ${minutes}m`;
            } else if (minutes > 0) {
                return `${minutes}m ${seconds}s`;
            } else {
                return `${seconds}s`;
            }
        }
        return timeString;
    }

    calculateProgress(current: number, target: number): number {
        return Math.min(100, Math.max(0, (current / target) * 100));
    }

    // ============== AUDIO COMPARISON UTILITIES ==============

    async compareAudioSimilarity(
        userAudioUrl: string,
        referenceAudioUrl: string,
    ): Promise<{
        similarity_score: number;
        detailed_comparison: {
            pitch_similarity: number;
            rhythm_similarity: number;
            phoneme_accuracy: number;
            overall_quality: number;
        };
        recommendations: string[];
    }> {
        const response = await api.post(`${baseURL}/compare-audio/`, {
            user_audio_url: userAudioUrl,
            reference_audio_url: referenceAudioUrl,
        });
        return response.data;
    }

    // ============== AVATAR PRACTICE UTILITIES ==============

    async getAvatarDemonstration(
        exerciseId: string,
        avatarType: '2d' | '3d' = '2d',
    ): Promise<{
        demonstration_video_url: string;
        phoneme_timings: Array<{
            phoneme: string;
            start_time: number;
            end_time: number;
            mouth_shape: string;
            tongue_position: string;
        }>;
        synchronized_audio_url: string;
    }> {
        const response = await api.get(`${baseURL}/avatar-demonstration/${exerciseId}/?avatar_type=${avatarType}`);
        return response.data;
    }
}

const speakingService = new SpeakingService();
export default speakingService;
